import React, { useEffect, useState, useContext, useCallback } from 'react';
import Layout from 'components/sales/layout/layout';
import localization from 'assets/localization/localization';
import { Button } from 'react-bootstrap';
import ConstantInput from 'components/sales/Input/constantInput/constantInput';
import DropDownInput from 'components/sales/Input/dropDownInput/dropDownInput';
import { BaseUrl } from 'contexts/baseUrl/baseUrl';
import ModalPop from 'components/sales/modal/modal';
import Images from 'assets/images/imageData';
import { useParams, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'services/interceptor/Interceptor';
import Config from 'services/config/config';
import {
  getVendorSubmissionTitle,
  getVendorApprovalSubmissionMessage,
  isApprovedVendorStatus,
} from '../vendorSubmissionMessages';
import {
  validateVendorBankBeneficiaryName,
  VENDOR_BENEFICIARY_NAME_MAX_LENGTH,
} from '../../vendorOnboarding/validation';
import './addVendorBankDetail.css';

const DEFAULT_COUNTRY = 'India';

const getValidationErrorMessage = (data, fallback = 'Failed to add bank detail') => {
  if (!data) return fallback;
  if (data.errors && typeof data.errors === 'object') {
    const messages = Object.values(data.errors).flat().filter(Boolean);
    if (messages.length) return messages.join(' ');
  }
  return data.message || fallback;
};

const INITIAL_FORM = {
  name: '',
  type: '',
  beneficiary_name: '',
  bank_account_no: '',
  ifsc_code: '',
  address: '',
  address2: '',
  country: DEFAULT_COUNTRY,
  geo_state_id: '',
  geo_city_id: '',
  geo_pincode_id: '',
  contact: '',
  currency_code_id: '',
  swift_code: '',
  iban: '',
};

const mapDropdownOptions = (items = [], labelKeys = ['name']) =>
  (items || []).map((item) => {
    const label =
      labelKeys.map((key) => item[key]).find((val) => val != null && val !== '') ||
      item.code ||
      item.pincode ||
      String(item.id);
    return { id: item.id, name: String(label) };
  });

const getCurrencyLabel = (currency) => {
  if (!currency) return '';
  if (currency.code && currency.description) {
    return `${currency.code} - ${currency.description}`;
  }
  return currency.code || currency.description || String(currency.id);
};

const AddVendorBankDetail = ({
  getVendorBankDetailFields,
  getGeoCitiesByState,
  getGeoPincodesByCity,
  getVendorOnboardingList,
  vendorOnboardedList,
}) => {
  const { baseUrl } = useContext(BaseUrl);
  const { vendorId } = useParams();
  const history = useHistory();

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isModal, setIsModal] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [bankTypes, setBankTypes] = useState([]);
  const [currencyLabel, setCurrencyLabel] = useState('INR');
  const [geoStates, setGeoStates] = useState([]);
  const [geoCities, setGeoCities] = useState([]);
  const [geoPincodes, setGeoPincodes] = useState([]);
  const [isApprovedVendor, setIsApprovedVendor] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const updateField = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    if (key === 'beneficiary_name') {
      const beneficiaryError = validateVendorBankBeneficiaryName(value);
      setFieldErrors((prev) => ({ ...prev, beneficiary_name: beneficiaryError }));
    }
  };

  const loadFieldOptions = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await getVendorBankDetailFields();

      if (!result?.success) {
        toast.error(result?.message || 'Failed to load bank detail fields');
        return;
      }

      const data = result.data || {};
      const mappedBankTypes = mapDropdownOptions(data.bank_types);
      const primaryBankType = mappedBankTypes.find(
        (item) => String(item.name).trim().toLowerCase() === 'primary'
      );
      setBankTypes(mappedBankTypes);
      setGeoStates(mapDropdownOptions(data.geo_states, ['name', 'state_name']));

      const currencies = data.currency_codes || [];
      const inrCurrency = currencies.find(
        (item) => String(item.code || '').toUpperCase() === 'INR'
      );
      setCurrencyLabel(getCurrencyLabel(inrCurrency) || 'INR');
      setFormData((prev) => ({
        ...prev,
        country: DEFAULT_COUNTRY,
        currency_code_id: inrCurrency?.id ?? '',
        type: primaryBankType?.id ?? '',
      }));
    } finally {
      setIsLoading(false);
    }
  }, [getVendorBankDetailFields]);

  const loadCities = useCallback(
    async (geoStateId) => {
      if (!geoStateId) {
        setGeoCities([]);
        setGeoPincodes([]);
        return;
      }
      setIsLoading(true);
      try {
        const result = await getGeoCitiesByState(geoStateId);
        if (result?.success) {
          setGeoCities(mapDropdownOptions(result.data, ['name', 'city_name']));
        } else {
          setGeoCities([]);
          toast.error(result?.message || 'Failed to load cities');
        }
        setGeoPincodes([]);
      } finally {
        setIsLoading(false);
      }
    },
    [getGeoCitiesByState]
  );

  const loadPincodes = useCallback(
    async (geoCityId) => {
      if (!geoCityId) {
        setGeoPincodes([]);
        return;
      }
      setIsLoading(true);
      try {
        const result = await getGeoPincodesByCity(geoCityId);
        if (result?.success) {
          setGeoPincodes(mapDropdownOptions(result.data, ['pincode', 'name']));
        } else {
          setGeoPincodes([]);
          toast.error(result?.message || 'Failed to load pincodes');
        }
      } finally {
        setIsLoading(false);
      }
    },
    [getGeoPincodesByCity]
  );

  useEffect(() => {
    loadFieldOptions();
  }, [loadFieldOptions]);

  useEffect(() => {
    if (getVendorOnboardingList) {
      (async () => {
        await getVendorOnboardingList(null, 1, false, vendorId);
      })();
    }
  }, [getVendorOnboardingList, vendorId]);

  useEffect(() => {
    const vendor = vendorOnboardedList?.data?.[0];
    if (vendor) {
      setIsApprovedVendor(isApprovedVendorStatus(vendor.approval_status));
    }
  }, [vendorOnboardedList]);

  const handleStateChange = async (val) => {
    const stateId = val?.id ?? '';
    setFormData((prev) => ({
      ...prev,
      geo_state_id: stateId,
      geo_city_id: '',
      geo_pincode_id: '',
    }));
    await loadCities(stateId);
  };

  const handleCityChange = async (val) => {
    const cityId = val?.id ?? '';
    setFormData((prev) => ({
      ...prev,
      geo_city_id: cityId,
      geo_pincode_id: '',
    }));
    await loadPincodes(cityId);
  };

  const handleSubmit = async () => {
    if (!formData.name?.trim()) {
      toast.error('Bank name is required');
      return;
    }
    if (!formData.type) {
      toast.error('Bank type is required');
      return;
    }
    if (!formData.bank_account_no?.trim()) {
      toast.error('Bank account number is required');
      return;
    }
    if (!formData.ifsc_code?.trim()) {
      toast.error('IFSC code is required');
      return;
    }
    if (!formData.address?.trim()) {
      toast.error('Address line 1 is required');
      return;
    }
    if (!formData.geo_state_id) {
      toast.error('State is required');
      return;
    }
    if (!formData.geo_city_id) {
      toast.error('City is required');
      return;
    }
    if (!formData.geo_pincode_id) {
      toast.error('Pincode is required');
      return;
    }
    if (!formData.currency_code_id) {
      toast.error('Currency is required');
      return;
    }

    const beneficiaryError = validateVendorBankBeneficiaryName(formData.beneficiary_name);
    if (beneficiaryError) {
      setFieldErrors((prev) => ({ ...prev, beneficiary_name: beneficiaryError }));
      toast.error('Please fix validation errors before submitting');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        vendor_id: Number(vendorId),
        name: formData.name.trim(),
        type: formData.type,
        beneficiary_name: formData.beneficiary_name?.trim() || undefined,
        bank_account_no: formData.bank_account_no.trim(),
        ifsc_code: formData.ifsc_code.trim(),
        address: formData.address.trim(),
        address2: formData.address2?.trim() || undefined,
        country: DEFAULT_COUNTRY,
        geo_state_id: formData.geo_state_id,
        geo_city_id: formData.geo_city_id,
        geo_pincode_id: formData.geo_pincode_id,
        contact: formData.contact?.trim() || undefined,
        currency_code_id: formData.currency_code_id,
        swift_code: formData.swift_code?.trim() || undefined,
        iban: formData.iban?.trim() || undefined,
      };

      const res = await axios.post(Config.preSalesList.addVendorBankDetail, payload);

      if (res?.data?.status === 0) {
        setIsModal(true);
        return;
      }

      toast.error(getValidationErrorMessage(res?.data));
    } catch (error) {
      toast.error(
        getValidationErrorMessage(error?.response?.data, error?.message || 'Failed to add bank detail')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalHide = () => {
    setIsModal(false);
    history.push(`${baseUrl}/vendorOnboardedList`);
  };

  return (
    <Layout
      className="add-vendor-bank-detail-page"
      title="Add Bank Detail"
      isLoading={isLoading || isSubmitting}
      isGoback
      linkFlag
      linkUrl={`${baseUrl}/vendorOnboardedList`}
      useNewHeaderLayout
    >
      <div className="card add-vendor-bank-detail-card">
        <div className="card-header">Bank Details</div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-4">
              <ConstantInput
                label="Bank Name"
                type="text"
                value={formData.name}
                handleChange={(val) => updateField('name', val)}
                required
                isStarRequired
              />
            </div>
            <div className="col-md-4">
              <DropDownInput
                label="Bank Type"
                data={bankTypes}
                value={
                  bankTypes.find((item) => String(item.id) === String(formData.type))
                    ?.name ?? 'Primary'
                }
                required
                isStarRequired
                getIdValue={(val) => updateField('type', val?.id ?? '')}
              />
            </div>
            <div className="col-md-4">
              <ConstantInput
                label="Beneficiary Name"
                type="text"
                value={formData.beneficiary_name}
                handleChange={(val) => updateField('beneficiary_name', val)}
                maxLength={VENDOR_BENEFICIARY_NAME_MAX_LENGTH}
                errorMsg={fieldErrors.beneficiary_name}
                blockError={Boolean(fieldErrors.beneficiary_name)}
              />
            </div>
            <div className="col-md-4">
              <ConstantInput
                label="Bank Account No"
                type="text"
                value={formData.bank_account_no}
                handleChange={(val) =>
                  updateField('bank_account_no', String(val ?? '').toUpperCase())
                }
                required
                isStarRequired
              />
            </div>
            <div className="col-md-4">
              <ConstantInput
                label="IFSC Code"
                type="text"
                value={formData.ifsc_code}
                handleChange={(val) =>
                  updateField('ifsc_code', String(val ?? '').toUpperCase())
                }
                required
                isStarRequired
              />
            </div>
            <div className="col-md-4">
              <ConstantInput
                label="Currency"
                type="text"
                value={currencyLabel}
                disabled
                required
                isStarRequired
              />
            </div>
            <div className="col-md-4">
              <ConstantInput
                label="Address Line 1"
                type="text"
                value={formData.address}
                handleChange={(val) => updateField('address', val)}
                required
                isStarRequired
              />
            </div>
            <div className="col-md-4">
              <ConstantInput
                label="Address Line 2"
                type="text"
                value={formData.address2}
                handleChange={(val) => updateField('address2', val)}
              />
            </div>
            <div className="col-md-4">
              <ConstantInput
                label="Country"
                type="text"
                value={formData.country}
                disabled
                required
                isStarRequired
              />
            </div>
            <div className="col-md-4">
              <DropDownInput
                label={localization.geoState}
                data={geoStates}
                required
                isStarRequired
                getIdValue={handleStateChange}
              />
            </div>
            <div className="col-md-4">
              <DropDownInput
                label={localization.geoCity}
                data={geoCities}
                disabled={!formData.geo_state_id}
                required
                isStarRequired
                getIdValue={handleCityChange}
              />
            </div>
            <div className="col-md-4">
              <DropDownInput
                label={localization.pinCode}
                data={geoPincodes}
                disabled={!formData.geo_city_id}
                required
                isStarRequired
                getIdValue={(val) => updateField('geo_pincode_id', val?.id ?? '')}
              />
            </div>
            <div className="col-md-4">
              <ConstantInput
                label="Contact"
                type="number"
                maxLength={10}
                minLength={10}
                tenDigitsValidation={true}
                errorMsg={localization.phoneError}
                value={formData.contact}
                handleChange={(val) => updateField('contact', val)}
              />
            </div>
            <div className="col-md-4">
              <ConstantInput
                label="SWIFT Code"
                type="text"
                value={formData.swift_code}
                handleChange={(val) => updateField('swift_code', val)}
              />
            </div>
            <div className="col-md-4">
              <ConstantInput
                label="IBAN"
                type="text"
                value={formData.iban}
                handleChange={(val) => updateField('iban', val)}
              />
            </div>
            <div className="col-md-12 add-vendor-bank-detail-actions">
              <Button onClick={handleSubmit} disabled={isSubmitting}>
                {localization.submitDtl}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <ModalPop
        title={getVendorSubmissionTitle(isApprovedVendor)}
        show={isModal}
        handleModalHide={handleModalHide}
        keyboard
      >
        <div className="text-center">
          <div className="success">
            <img src={Images?.tick} alt="" />
          </div>
          <h5>{getVendorApprovalSubmissionMessage('add_bank', isApprovedVendor)}</h5>
        </div>
      </ModalPop>
    </Layout>
  );
};

export default AddVendorBankDetail;
